import { useState } from 'react';
import { MenuItem, FormControl, Select, SelectChangeEvent, Typography } from '@mui/material';
import clsx from 'clsx';
import {
  DefaultEventCategoryId,
  defaultEventCategorySet,
  EventCategorySet,
} from '@demind-inc/core';

import './EventCategories.scss';
import { getCssVariable } from '../../../../utils';
import { getDefaultCategoryEventIconSet } from '../../../common';
import { CategoryOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getDefaultEventCategoryTitle } from '../../../DataView/i18n';

interface EventCategoriesProps {
  className?: string;
  selectedCategory: EventCategorySet;
  disabled?: boolean;
  setSelectedCategory: (selectedCategories: EventCategorySet) => void;
}

const colorGrayBase = getCssVariable('--color-gray-base');
const colorBlack = getCssVariable('--color-black');
const colorGrayMedium = getCssVariable('--color-gray-medium');

export default function EventCategories({
  className,
  selectedCategory,
  setSelectedCategory,
  disabled,
}: EventCategoriesProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const category = defaultEventCategorySet.find((item) => item.id === event.target.value);
    if (category) {
      setSelectedCategory(category);
    }
  };

  return (
    <div className={clsx('event-categories', className)}>
      <CategoryOutlined className="event-categories__icon" />
      <FormControl className="event-categories__form-control">
        <Select
          value={selectedCategory?.id ?? 'default'}
          disabled={disabled}
          onChange={handleChange}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          MenuProps={{
            PaperProps: {
              className: 'event-categories__menu-paper',
            },
          }}
          renderValue={(selected) => {
            if (selected === 'default') {
              return <Typography>{t('routine.modal.selectCategory')}</Typography>;
            }

            const category = defaultEventCategorySet.find((c) => c.id === selected);
            return (
              <Typography>
                {category
                  ? getDefaultEventCategoryTitle(
                      t,
                      category.id as DefaultEventCategoryId,
                      category.name
                    )
                  : ''}
              </Typography>
            );
          }}
          className={clsx('event-categories__select', { 'event-categories__select--open': open })}
        >
          <MenuItem value="default" disabled>
            <Typography>{t('routine.modal.selectCategory')}</Typography>
          </MenuItem>
          {defaultEventCategorySet.map((category) => (
            <MenuItem key={category.id} value={category.id} className="event-categories__menu-item">
              {getDefaultCategoryEventIconSet(
                category.id as DefaultEventCategoryId,
                16,
                category.id === selectedCategory?.id ? colorBlack : colorGrayBase
              )}
              <Typography>
                {getDefaultEventCategoryTitle(
                  t,
                  category.id as DefaultEventCategoryId,
                  category.name
                )}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
}
